import { useState } from 'react'
import { createVisitOnServer, listTemplates, revokeVisitSessions, saveTemplate } from '../api'
import type { ChecklistTemplate, TemplateKind } from '../types'
import { templates } from '../data/templates'

export default function AdminPage() {
  const [form, setForm] = useState({
    adminPassword: '',
    client: '',
    siteName: '',
    address: '',
    templateId: 'buildings' as TemplateKind,
    visitDate: new Date().toISOString().slice(0, 10),
  })
  const [result, setResult] = useState<{ slug: string; url: string } | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [revokeSlug, setRevokeSlug] = useState('')
  const [revokeMessage, setRevokeMessage] = useState('')
  const [templateOptions, setTemplateOptions] = useState<ChecklistTemplate[]>(Object.values(templates))
  const [templateEditor, setTemplateEditor] = useState<ChecklistTemplate | null>(null)
  const [templateMessage, setTemplateMessage] = useState('')

  const update = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      setResult(await createVisitOnServer(form))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The visit could not be created.')
    } finally {
      setBusy(false)
    }
  }

  const loadTemplateOptions = async () => {
    setTemplateMessage('')
    try {
      const result = await listTemplates(form.adminPassword)
      setTemplateOptions(result.templates)
      setTemplateMessage('Latest immutable template versions loaded.')
    } catch (caught) {
      setTemplateMessage(caught instanceof Error ? caught.message : 'Templates could not be loaded.')
    }
  }

  const editTemplate = (duplicate: boolean) => {
    const selected = templateOptions.find((item) => item.id === form.templateId)
    if (!selected) return
    const copy = structuredClone(selected)
    if (duplicate) {
      copy.id = `custom-${crypto.randomUUID().slice(0, 8)}`
      copy.label = `${copy.label} copy`
      copy.version = 0
    }
    setTemplateEditor(copy)
    setTemplateMessage('')
  }

  const saveEditedTemplate = async () => {
    if (!templateEditor) return
    setTemplateMessage('')
    try {
      const result = await saveTemplate(form.adminPassword, templateEditor)
      setTemplateOptions((current) => [
        ...current.filter((item) => item.id !== result.template.id),
        result.template,
      ].sort((a, b) => a.label.localeCompare(b.label)))
      update('templateId', result.template.id)
      setTemplateEditor(result.template)
      setTemplateMessage(`Saved ${result.template.label} version ${result.template.version}. Existing visits are unchanged.`)
    } catch (caught) {
      setTemplateMessage(caught instanceof Error ? caught.message : 'The template could not be saved.')
    }
  }

  return (
    <main className="admin-page">
      <a className="brand brand--standalone" href="/">
        <img src="/climatise-logo.svg" alt="" />
        <span>Climatise</span>
      </a>
      <section className="admin-card">
        <span className="eyebrow">Visit administration</span>
        <h1>Create a site visit</h1>
        <p>One hosted app serves every client. This creates a private visit link from a frozen template version.</p>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Admin password
            <input type="password" value={form.adminPassword} onChange={(event) => update('adminPassword', event.target.value)} required />
          </label>
          <div className="field-grid field-grid--two">
            <label>
              Client
              <input value={form.client} onChange={(event) => update('client', event.target.value)} required />
            </label>
            <label>
              Site name
              <input value={form.siteName} onChange={(event) => update('siteName', event.target.value)} required />
            </label>
          </div>
          <label>
            Site address
            <textarea rows={2} value={form.address} onChange={(event) => update('address', event.target.value)} />
          </label>
          <div className="field-grid field-grid--two">
            <label>
              Checklist template
              <select value={form.templateId} onChange={(event) => update('templateId', event.target.value)}>
                {templateOptions.map((template) => (
                  <option key={template.id} value={template.id}>{template.label}</option>
                ))}
              </select>
            </label>
            <label>
              Visit date
              <input type="date" value={form.visitDate} onChange={(event) => update('visitDate', event.target.value)} />
            </label>
          </div>
          <p className="section-intro">Anyone with the private link can enter their name and open the complete visit. Share the link only with the audit team and approved client contacts.</p>
          {error && <p className="alert alert--danger" role="alert">{error}</p>}
          <button className="button" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create visit'}</button>
        </form>
        {result && (
          <div className="created-visit" aria-live="polite">
            <strong>Visit created</strong>
            <a href={result.url}>{result.url}</a>
            <button type="button" className="button button--secondary" onClick={() => void navigator.clipboard.writeText(result.url)}>
              Copy URL
            </button>
          </div>
        )}
        <details className="details-card admin-rotate">
          <summary>Revoke existing visit sessions</summary>
          <form
            className="details-card__body"
            onSubmit={(event) => {
              event.preventDefault()
              setRevokeMessage('')
              void revokeVisitSessions({
                adminPassword: form.adminPassword,
                slug: revokeSlug,
              }).then(() => {
                setRevokeMessage('Existing sessions revoked. Contributors must enter their name again.')
                setRevokeSlug('')
              }).catch((caught: unknown) => {
                setRevokeMessage(caught instanceof Error ? caught.message : 'Session revocation failed.')
              })
            }}
          >
            <label>
              Visit slug
              <input value={revokeSlug} onChange={(event) => setRevokeSlug(event.target.value)} required />
            </label>
            <button className="button button--secondary" type="submit">Revoke sessions</button>
            {revokeMessage && <p className="alert">{revokeMessage}</p>}
          </form>
        </details>
        <details className="details-card admin-rotate">
          <summary>Manage checklist templates</summary>
          <div className="details-card__body template-admin">
            <p>Load the latest versions, edit a template or make a separate copy. Saving creates a new immutable version.</p>
            <div className="inline-actions">
              <button type="button" className="button button--secondary" onClick={() => void loadTemplateOptions()}>Load latest templates</button>
              <button type="button" className="button button--secondary" onClick={() => editTemplate(false)}>Edit selected</button>
              <button type="button" className="button button--secondary" onClick={() => editTemplate(true)}>Duplicate selected</button>
            </div>
            {templateEditor && (
              <div className="template-editor">
                <div className="field-grid field-grid--two">
                  <label>
                    Template name
                    <input
                      value={templateEditor.label}
                      onChange={(event) => setTemplateEditor((current) => current && ({ ...current, label: event.target.value }))}
                    />
                  </label>
                  <label>
                    Template key
                    <input value={templateEditor.id} readOnly />
                  </label>
                </div>
                <label>
                  Description
                  <textarea
                    rows={2}
                    value={templateEditor.description}
                    onChange={(event) => setTemplateEditor((current) => current && ({ ...current, description: event.target.value }))}
                  />
                </label>
                {templateEditor.categories.map((category, index) => (
                  <section className="template-category-editor" key={category.id}>
                    <div className="template-category-editor__heading">
                      <strong>{index + 1}. {category.label || 'Untitled category'}</strong>
                      <div className="inline-actions">
                        <button type="button" disabled={index === 0} onClick={() => setTemplateEditor((current) => {
                          if (!current || index === 0) return current
                          const categories = [...current.categories]
                          ;[categories[index - 1], categories[index]] = [categories[index], categories[index - 1]]
                          return { ...current, categories }
                        })}>↑</button>
                        <button type="button" disabled={index === templateEditor.categories.length - 1} onClick={() => setTemplateEditor((current) => {
                          if (!current || index === current.categories.length - 1) return current
                          const categories = [...current.categories]
                          ;[categories[index], categories[index + 1]] = [categories[index + 1], categories[index]]
                          return { ...current, categories }
                        })}>↓</button>
                        <button type="button" onClick={() => setTemplateEditor((current) => current && ({
                          ...current,
                          categories: current.categories.filter((item) => item.id !== category.id),
                        }))}>Remove</button>
                      </div>
                    </div>
                    <label>
                      Category name
                      <input value={category.label} onChange={(event) => setTemplateEditor((current) => current && ({
                        ...current,
                        categories: current.categories.map((item) => item.id === category.id ? { ...item, label: event.target.value } : item),
                      }))} />
                    </label>
                    <label>
                      Guidance
                      <textarea rows={2} value={category.description} onChange={(event) => setTemplateEditor((current) => current && ({
                        ...current,
                        categories: current.categories.map((item) => item.id === category.id ? { ...item, description: event.target.value } : item),
                      }))} />
                    </label>
                    <label>
                      Walk prompts — one per line
                      <textarea rows={4} value={category.prompts.map((item) => item.label).join('\n')} onChange={(event) => setTemplateEditor((current) => current && ({
                        ...current,
                        categories: current.categories.map((item) => item.id === category.id ? {
                          ...item,
                          prompts: event.target.value.split('\n').filter(Boolean).map((label, promptIndex) => ({
                            id: `${category.id}-prompt-${promptIndex + 1}`,
                            label,
                          })),
                        } : item),
                      }))} />
                    </label>
                    <label>
                      Suggested data points — one per line
                      <textarea rows={3} value={category.dataPrompts.join('\n')} onChange={(event) => setTemplateEditor((current) => current && ({
                        ...current,
                        categories: current.categories.map((item) => item.id === category.id ? {
                          ...item,
                          dataPrompts: event.target.value.split('\n').filter(Boolean),
                        } : item),
                      }))} />
                    </label>
                  </section>
                ))}
                <div className="inline-actions">
                  <button type="button" className="button button--secondary" onClick={() => setTemplateEditor((current) => current && ({
                    ...current,
                    categories: [...current.categories, {
                      id: `category-${crypto.randomUUID().slice(0, 8)}`,
                      label: 'New category',
                      description: '',
                      prompts: [],
                      dataPrompts: [],
                    }],
                  }))}>Add category</button>
                  <button type="button" className="button" onClick={() => void saveEditedTemplate()}>Save new version</button>
                </div>
              </div>
            )}
            {templateMessage && <p className="alert" role="status">{templateMessage}</p>}
          </div>
        </details>
      </section>
    </main>
  )
}
